/**
 * MaturityLadder — the client's bonds by redemption year, and the coupon they are projected to
 * pay (TASK-907).
 *
 * `Show Schneider's bond maturity ladder and expected coupon income for the next 12 months`
 * refused honestly and stayed undoable, on a client who is **31.9% fixed income** with the
 * coupon and the maturity written into every instrument name.
 *
 * **What cannot be parsed is excluded and COUNTED, never bucketed at zero.** A ladder that
 * silently drops a bond understates redemptions, which is the one thing it exists to show.
 * Schneider's `iShares USD Treas. ETF` has no coupon and no maturity: CHF 275'244 of fixed
 * income that belongs in the total and in no year. The buckets plus the excluded value equal
 * the fixed-income total exactly, and the widget states all three.
 *
 * **The income is projected on MARKET VALUE.** A coupon is paid on the nominal, and this book
 * records none — `quantity` is 1 and `market_price` is the whole position. "Projected" without
 * saying on what would leave the RM to assume a nominal was used.
 */

import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import { httpClient } from '../../../lib/httpClient';
import { WidgetErrorState } from '../../ui/WidgetErrorState';
import { WidgetContainer } from '../WidgetContainer';
import { OpenAsSpreadsheet } from './OpenAsSpreadsheet';

const chf = (v: number) =>
  new Intl.NumberFormat('de-CH', {
    style: 'currency', currency: 'CHF', maximumFractionDigits: 0,
  }).format(v);

const MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export interface LadderBucket {
  year: number;
  value_chf: number;
  position_count: number;
}

export interface LadderPosition {
  position_id: string;
  isin?: string | null;
  security_name?: string | null;
  coupon_pct: number;
  maturity: string;
  maturity_year: number;
  value_chf: number;
  next_payment_month: number;
  income_chf: number;
  accrued_interest_chf: number | null;
  /** `parsed_from_name` or `field` — the ticket asks for the provenance, not just the number. */
  terms_basis: string;
}

export interface ExcludedPosition {
  position_id: string;
  isin?: string | null;
  security_name?: string | null;
  value_chf: number;
  reason: string;
}

export interface MaturityLadderResponse {
  client_id: string;
  months: number;
  buckets: LadderBucket[];
  positions: LadderPosition[];
  excluded: ExcludedPosition[];
  excluded_count: number;
  excluded_value_chf: number;
  fixed_income_total_chf: number;
  laddered_total_chf: number;
  projected_income_chf: number;
  income_basis?: string;
  unpriceable_currencies?: string[];
  timestamp?: string | null;
}

export interface MaturityLadderProps {
  clientName?: string;
  data: MaturityLadderResponse | null;
  isLoading?: boolean;
  isError?: boolean;
  error?: Error | null;
  onRetry?: () => void;
}

export function MaturityLadder({
  clientName, data, isLoading = false, isError = false, error = null, onRetry,
}: MaturityLadderProps) {
  const months = data?.months ?? 12;

  const body = () => {
    if (isLoading) {
      return (
        <div className="flex items-center gap-2 p-6 text-xs text-muted-foreground"
             data-testid="ladder-loading">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Reading the bonds…
        </div>
      );
    }

    if (isError) {
      return (
        <WidgetErrorState
          title={`Couldn't build ${clientName ?? 'this client'}'s maturity ladder`}
          detail={error?.message}
          onRetry={onRetry}
        />
      );
    }

    const buckets = data?.buckets ?? [];
    const positions = data?.positions ?? [];

    if (buckets.length === 0 && (data?.excluded_count ?? 0) === 0) {
      return (
        <p className="p-6 text-xs text-muted-foreground" data-testid="ladder-empty">
          {clientName ?? 'This client'} holds no fixed income.
        </p>
      );
    }

    const peak = Math.max(1, ...buckets.map((b) => b.value_chf));

    return (
      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2 pb-2"
             data-testid="ladder-header">
          <p className="text-xs font-medium">
            {clientName ?? 'Client'}
            <span className="text-muted-foreground">
              {' · '}fixed income {chf(data?.fixed_income_total_chf ?? 0)}
            </span>
          </p>
          <p className="text-[10px] text-muted-foreground">
            projected {months}m coupon {chf(data?.projected_income_chf ?? 0)}
          </p>
        </div>

        {/* The ladder. A bar per year, because the shape is the answer. */}
        <div className="space-y-1 pb-3" data-testid="ladder-buckets">
          {buckets.map((b) => (
            <div key={b.year} className="flex items-center gap-2 text-[11px]"
                 data-testid="ladder-bucket" data-year={b.year}>
              <span className="w-10 tabular-nums text-muted-foreground">{b.year}</span>
              <span className="h-2 rounded-sm bg-primary/30"
                    style={{ width: `${Math.max(2, (b.value_chf / peak) * 55)}%` }} />
              <span className="tabular-nums">{chf(b.value_chf)}</span>
              <span className="text-[10px] text-muted-foreground">
                {b.position_count} position{b.position_count === 1 ? '' : 's'}
              </span>
            </div>
          ))}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-[11px]" data-testid="ladder-table">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="py-1 text-left font-normal">security</th>
                <th className="py-1 text-left font-normal">isin</th>
                <th className="py-1 text-right font-normal">coupon</th>
                <th className="py-1 text-left font-normal">maturity</th>
                <th className="py-1 text-right font-normal">CHF</th>
                <th className="py-1 text-left font-normal">next payment</th>
                <th className="py-1 text-right font-normal">{months}m income</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((p) => (
                <tr key={p.position_id} data-testid="ladder-row"
                    data-position-id={p.position_id}
                    className="border-b border-border/40 last:border-0">
                  <td className="max-w-[200px] truncate py-1" title={p.security_name ?? ''}>
                    {p.security_name}
                  </td>
                  <td className="py-1 font-mono text-[10px]">{p.isin ?? '—'}</td>
                  <td className="py-1 text-right tabular-nums">{p.coupon_pct.toFixed(3)}%</td>
                  <td className="py-1 tabular-nums">{p.maturity}</td>
                  <td className="py-1 text-right tabular-nums">{chf(p.value_chf)}</td>
                  <td className="py-1">{MONTHS[p.next_payment_month] ?? '—'}</td>
                  <td className="py-1 text-right tabular-nums">{chf(p.income_chf)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* The honesty rule, stated rather than implied. */}
        {(data?.excluded_count ?? 0) > 0 && (
          <div className="pt-2 text-[10px] text-muted-foreground" data-testid="ladder-excluded">
            {data!.excluded_count} position{data!.excluded_count === 1 ? '' : 's'} worth
            {' '}{chf(data!.excluded_value_chf)} could not be laddered and {'are'} excluded from
            {' '}the buckets and the income:
            {' '}{data!.excluded.map((e) => e.security_name).join(', ')}.
            {' '}The buckets total {chf(data!.laddered_total_chf)} of
            {' '}{chf(data!.fixed_income_total_chf)} fixed income.
          </div>
        )}

        <p className="pt-1 text-[10px] text-muted-foreground" data-testid="ladder-footer">
          Income is <strong>projected</strong>, not realised, and is calculated on market value
          {' '}— this book records no nominal. Coupon and maturity are
          {' '}{positions.every((p) => p.terms_basis === 'parsed_from_name')
            ? 'read from the instrument name'
            : 'taken from the instrument record where present, otherwise read from its name'}.
          {' '}The payment month is derived from the maturity date, not a custodian schedule.
        </p>
      </div>
    );
  };

  return (
    <WidgetContainer
      source="Portfolio · Maturity ladder"
      dataAsOf={data?.timestamp ?? null}
      toolbar={
        (data?.positions?.length ?? 0) > 0 ? (
          <OpenAsSpreadsheet
            sourceWidget="MaturityLadder"
            clientId={data?.client_id}
            asOf={data?.timestamp ?? null}
            build={() => ({
              title: `Maturity ladder — ${clientName ?? 'client'} (${months}m income)`,
              sheetName: 'Ladder',
              columns: [
                { header: 'Security', width: 40 },
                { header: 'ISIN', width: 16 },
                { header: 'Coupon %', width: 12 },
                { header: 'Maturity', width: 14 },
                { header: 'CHF', fmt: 'currency', width: 16 },
                { header: `${months}m income CHF`, fmt: 'currency', width: 18 },
                { header: 'Terms from', width: 18 },
                { header: 'position_id', width: 38 },
              ],
              rows: (data?.positions ?? []).map((p) => [
                p.security_name ?? '', p.isin ?? '', p.coupon_pct, p.maturity,
                p.value_chf, p.income_chf, p.terms_basis, p.position_id,
              ]),
            })}
          />
        ) : undefined
      }
    >
      {body()}
    </WidgetContainer>
  );
}

export interface MaturityLadderPanelProps {
  clientId: string;
  clientName?: string;
  filters?: { months?: number };
  months?: number;
}

export function MaturityLadderPanel({
  clientId, clientName, filters, months,
}: MaturityLadderPanelProps) {
  const horizon = filters?.months ?? months ?? 12;

  const query = useQuery({
    queryKey: ['maturity-ladder', clientId, horizon],
    queryFn: () => httpClient.get<MaturityLadderResponse>(
      `/api/portfolio/maturity-ladder/${clientId}?months=${horizon}`,
    ),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  return (
    <MaturityLadder
      clientName={clientName}
      data={query.data ?? null}
      isLoading={query.isLoading}
      isError={query.isError}
      error={query.error as Error | null}
      onRetry={() => void query.refetch()}
    />
  );
}

export default MaturityLadderPanel;
